/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/**
 * FuzzyVRMGrid.js
 * @description Grid component for displaying VRM event data with pagination, filtering, and sorting.
 * Uses AG-Grid for data virtualization and infinite scrolling.
 * Delegates data fetching logic to the useGridData hook.
 *
 * @component
 * @example
 * return (
 *   <FuzzyVRMGrid
 *     gridRef={gridRef}
 *     preCaseId="12345"
 *     onViewImageClick={handleViewImage}
 *     perPageSize={100}
 *     onPageSizeChange={setPerPageSize}
 *   />
 * )
 */
import React from 'react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import Grid from '../../../../../components/base/grid/Grid'
import GridPageSize from '../../../../../components/custom/gridPageSize/GridPageSize'
import { useGridData } from '../hooks'
import { getGridColumns, DEFAULT_PAGE_SIZE, GRID_CONFIG } from '../constants'

/**
 * @param {Object} props - Component props
 * @param {Object} props.gridRef - Reference to the grid DOM element
 * @param {string} props.preCaseId - The ID of the pre-case to load data for
 * @param {Function} props.onViewImageClick - Callback when view image button is clicked
 * @param {number} props.perPageSize - Number of rows per page
 * @param {Function} props.onPageSizeChange - Callback to update page size in parent
 * @returns {JSX.Element} The rendered grid component with pagination controls
 */
const FuzzyVRMGrid = ({
  gridRef,
  preCaseId,
  onViewImageClick,
  perPageSize,
  onPageSizeChange,
}) => {
  const {
    onGridReady,
    onRefreshClickHandler,
    onPageSizeChange: handlePageSizeChange,
  } = useGridData(gridRef, preCaseId)

  const handleChangePageSize = (event, params) => {
    const ddlValue = params?.value
    handlePageSizeChange(parseInt(ddlValue), onPageSizeChange)
  }

  const columns = getGridColumns(onViewImageClick)

  return (
    <>
      <div className="headerSection">
        <div className="divPagesize">
          <GridPageSize
            onPageSizeHandler={handleChangePageSize}
            isRefreshEnabled={true}
            onRefreshClickHandler={onRefreshClickHandler}
            timezoneType="case"
          />
        </div>
      </div>

      <Grid
        dataSource={{ columnDefs: columns, rowdata: [] }}
        columnDefs={columns}
        listviewergridref={gridRef}
        rowModelType="infinite"
        onGridReady={onGridReady}
        pagination={true}
        paginationPageSize={perPageSize}
        cacheBlockSize={perPageSize}
        rowHeight={GRID_CONFIG.rowHeight}
        defaultColDef={GRID_CONFIG.defaultColDef}
      />
    </>
  )
}

export default FuzzyVRMGrid
